<script lang="ts">
	import { Text, FlexibleCard, Modal, SelectCard, CardGrid } from '@Shelly/shelly-design-system';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';

	import { home_data } from 'src/stores/account/user';
import { createMemberStatusSnapshot } from 'src/stores/device-runtime/status-selectors';
import { devices } from 'src/stores/lists/devices';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import CameraMonitoringDashboard from './MonitoringDashboard/CameraMonitoringDashboard.svelte';
	import CameraMonitoringToolbar from './MonitoringDashboard/CameraMonitoringToolbar.svelte';
	import CameraSettingsModal from './MonitoringDashboard/CameraSettingsModal.svelte';
	import { format } from 'svelte-i18n';
	import {
		getCameraMonitoringSettings,
		saveCameraMonitoringSettings,
		defaultCameraMonitoringSettings,
		type GridLayout,
	} from './helpers/camera-monitoring-settings';
	import { getCameraDevices, isCameraOnline } from './helpers/camera-helpers';

	// Grid layout options
	const allGridLayouts: { value: GridLayout; icon: string; cols: number }[] = [
		{ value: '1x1', icon: 'fa-solid fa-square', cols: 1 },
		{ value: '2x2', icon: 'fa-solid fa-grid-2', cols: 2 },
		{ value: '3x3', icon: 'fa-solid fa-grid-3', cols: 3 },
		{ value: '4x4', icon: 'fa-solid fa-grid-4', cols: 4 },
	];

	let showSettingsModal = false;
	let editMode = false;
	let showModalMenuGrouped = false;
	let windowInnerWidth = 0;

	// DS breakpoints:
	$: isMobile = windowInnerWidth < SystemScreenBreakpoints.MOBILE_MAX;
	$: isLargeDesktop = windowInnerWidth >= SystemScreenBreakpoints.DESKTOP_SMALL;

	// Dropdown menu data structure (like DS demo pattern)
	$: dropdownActionsData = [
		{
			groupTitle: $format('dropdown_group_dashboard', { default: 'Dashboard' }),
			isGroupTitle: true,
			items: [
				{
					id: 'camera-settings',
					title: $format('dropdown_customize_dashboard', { default: 'Customize' }),
					icon: 'fas fa-sliders',
					action: openSettings,
				},
				{
					id: 'edit-order',
					title: $format('dropdown_reorder_cameras', { default: 'Reorder cameras' }),
					icon: 'fas fa-arrows-up-down-left-right',
					action: toggleEditMode,
				},
			],
		},
	];

	// Get all camera devices from store
	$: allCameraDevices = getCameraDevices($devices) || [];
	$: cameraIds = allCameraDevices.map((d) => d.id);
	$: memberStatusStore = createMemberStatusSnapshot(cameraIds);
	$: memberStatus = $memberStatusStore;

	// Derive settings reactively from home_data
	$: settings = getCameraMonitoringSettings($home_data);
	$: preferredLayout = settings.preferredLayout;
	$: hideOfflineCameras = settings.hideOfflineCameras;
	$: cameras = settings.cameras;

	// Derive visible camera IDs as Set for quick lookup (empty array = all cameras)
	$: visibleCameraIds = new Set(!cameras?.length ? allCameraDevices.map((d) => d.id) : cameras);

	// Derive camera order (use saved order, or default to all cameras)
	$: cameraOrder = !cameras?.length ? allCameraDevices.map((d) => d.id) : cameras;

	// Filter cameras based on visibility settings and online status, respecting order
	$: displayedCameras = cameraOrder
		.map((id) => allCameraDevices.find((d) => d.id === id))
		.filter((camera): camera is (typeof allCameraDevices)[0] => {
			if (!camera) return false;
			if (!visibleCameraIds.has(camera.id)) {
				return false;
			}
			if (hideOfflineCameras && !isCameraOnline(memberStatus[camera.id])) {
				return false;
			}
			return true;
		});

	// Dynamic grid layouts - by camera count and screen size (DS breakpoints)
	// Phone (≤639): 1 column only. 4-column only on large desktop (≥1280) where drawer doesn't crowd content
	$: availableGridLayouts = allGridLayouts.filter((layout) => {
		const cameraCount = displayedCameras.length;
		if (isMobile && layout.cols > 1) return false;
		if (!isLargeDesktop && layout.cols === 4) return false;
		if (layout.cols === 1) return true;
		if (layout.cols === 2) return cameraCount >= 2;
		if (layout.cols === 3) return cameraCount >= 5;
		if (layout.cols === 4) return cameraCount >= 7;
		return true;
	});

	// Effective layout: use preferred if available, otherwise fallback to largest available
	$: selectedLayout = availableGridLayouts.some((l) => l.value === preferredLayout)
		? preferredLayout
		: availableGridLayouts[availableGridLayouts.length - 1]?.value ||
			defaultCameraMonitoringSettings.preferredLayout;

	// Get current layout configuration
	$: currentLayout = allGridLayouts.find((l) => l.value === selectedLayout) || allGridLayouts[1];

	// Phone: 1 column only (DS breakpoint)
	$: effectiveCols = isMobile ? 1 : currentLayout.cols;

	function handleLayoutChange(layout: GridLayout) {
		saveCameraMonitoringSettings({ preferredLayout: layout });
	}

	function openSettings() {
		showSettingsModal = true;
		showModalMenuGrouped = false;
	}

	function closeSettings() {
		showSettingsModal = false;
		showModalMenuGrouped = false;
	}

	function handleSettingsSave(event: CustomEvent<{ cameras: string[]; hideOfflineCameras: boolean }>) {
		const { cameras: newCameras, hideOfflineCameras: newHideOffline } = event.detail;
		saveCameraMonitoringSettings({ cameras: newCameras, hideOfflineCameras: newHideOffline });
	}

	function toggleEditMode() {
		editMode = !editMode;
	}

	function handleGridReorder(event: CustomEvent) {
		// Dedupe defensively: a transient DnD glitch can momentarily yield the same
		// id twice, and persisting that would make the keyed grid crash on render.
		const orderedIds: string[] = event.detail.items.map((item: { id: string }) => item.id);
		const newOrder: string[] = [...new Set(orderedIds)];
		saveCameraMonitoringSettings({ cameras: newOrder });
	}
</script>

<svelte:window bind:innerWidth={windowInnerWidth} />

<div class="width-full pb-m">
	<CameraMonitoringToolbar
		{editMode}
		{availableGridLayouts}
		{selectedLayout}
		{dropdownActionsData}
		on:layout-change={(e) => handleLayoutChange(e.detail.layout)}
		on:toggle-edit={toggleEditMode}
	/>

	{#if allCameraDevices.length === 0}
		<FlexibleCard
			backgroundColor={$showNewLayout ? '--color-0' : undefined}
			header={$format('empty_no_cameras_found', { default: 'No cameras found' })}
			icon="fas fa-video-slash"
			padding="m"
		>
			<Text>
				{$format('empty_no_cameras_found_descr', {
					default:
						'No camera devices are currently available. Add cameras to your account to start monitoring.',
				})}
			</Text>
		</FlexibleCard>
	{:else}
		{#key selectedLayout}
			<CameraMonitoringDashboard
				cameras={displayedCameras}
				cols={effectiveCols}
				{editMode}
				onOpenSettings={openSettings}
				on:reorder={handleGridReorder}
			/>
		{/key}
	{/if}
</div>

<CameraSettingsModal
	showModal={showSettingsModal}
	{cameras}
	{hideOfflineCameras}
	on:close={closeSettings}
	on:save={handleSettingsSave}
/>

<Modal bind:showModal={showModalMenuGrouped} hideTitle on:close={() => (showModalMenuGrouped = false)}>
	<svelte:fragment slot="content">
		{#each dropdownActionsData as group, index}
			<Text styleType="label-large" class="mb-s">
				{group.groupTitle}
			</Text>
			<CardGrid md={250} noPaddingBottom gridGap="s">
				{#each group.items as item}
					<SelectCard
						cardBgColor={$showNewLayout ? 'color-1' : undefined}
						hideAction
						class="width-full"
						type="horizontal"
						title={item.title}
						icon={item.icon}
						on:click={item.action}
					/>
				{/each}
			</CardGrid>
			{#if index < dropdownActionsData.length - 1}
				<hr class="mb-l mt-l" />
			{/if}
		{/each}
	</svelte:fragment>
</Modal>
